import { useState, useCallback, useEffect, useRef } from 'react';
import useDeepgram from './useDeepgram';
import useClaude from './useClaude';
import useKokoro from './useKokoro';

/**
 * Remove email-draft blocks before text reaches the TTS engine, so Kokoro
 * speaks only the short spoken confirmation and never the full email body or
 * any of the marker characters. The block still appears in the transcript for
 * Dave to read.
 *
 * Handles the canonical <<DRAFT>> ... <<END_DRAFT>> block, the legacy
 * [EMAIL_DRAFT] ... [/EMAIL_DRAFT] block, and — as a safety net — a <<DRAFT>>
 * opener with no closing marker (strips through end of text).
 */
export function stripDraftBlocks(text) {
  if (!text) return '';
  return text
    // Canonical block: <<DRAFT>> ... <<END_DRAFT>> (also DRAFT_END / /DRAFT)
    .replace(/<<\s*DRAFT\s*>>[\s\S]*?<<\s*(?:END_DRAFT|DRAFT_END|\/DRAFT)\s*>>/gi, '')
    // Safety net: opener with no closer — strip from <<DRAFT>> to end of text
    .replace(/<<\s*DRAFT\s*>>[\s\S]*$/gi, '')
    // Legacy block format
    .replace(/\[EMAIL_DRAFT\][\s\S]*?\[\/EMAIL_DRAFT\]/gi, '')
    // Any stray standalone markers that slipped through
    .replace(/<<\s*(?:DRAFT|END_DRAFT|DRAFT_END|\/DRAFT)\s*>>/gi, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * useConversation — full-duplex voice conversation orchestrator.
 *
 * States: idle | ready | listening | processing | speaking
 *
 * Flow:
 *   idle → start() → listening (Deepgram + mic hot)
 *   listening → Deepgram final transcript → processing (sends to Claude)
 *   processing → Claude responds → speaking (ElevenLabs TTS)
 *   speaking → TTS done → listening (auto-resume mic)
 *   speaking → user speaks (interrupt) → listening (kills TTS, resumes mic)
 *   any → stop() → idle (full disconnect)
 */
export default function useConversation(settings = {}, { pendingImage, clearImage } = {}) {
  const [state, setState] = useState('idle');
  const stateRef = useRef(state);
  stateRef.current = state;

  // Mode: 'voice' or 'text'
  const [mode, setMode] = useState('voice');
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // User mute toggle (separate from auto-mute during TTS)
  const [userMuted, setUserMuted] = useState(false);
  const userMutedRef = useRef(false);

  const deepgram = useDeepgram();
  const claude = useClaude(settings);
  const tts = useKokoro({
    voiceId: settings.voiceId || 'JBFqnCBsd6RMkjVDRZzb',
  });

  const accumulatedTranscriptRef = useRef('');
  const mountCount = useRef(0);
  const autoSendTimerRef = useRef(null);

  // Wire STT callback — accumulate transcript, auto-send after silence
  useEffect(() => {
    mountCount.current++;
    const myMount = mountCount.current;
    console.log(`[Conversation] useEffect mount #${myMount}`);

    deepgram.onTranscriptRef.current = (text, isFinal) => {
      console.log(`[Conversation] transcript final=${isFinal} text="${text}" state=${stateRef.current}`);

      // If user has muted, ignore transcripts
      if (userMutedRef.current) return;

      if (isFinal && text.trim()) {
        // Interrupt: if TTS is playing and user starts speaking, kill it
        if (stateRef.current === 'speaking') {
          console.log('[Conversation] INTERRUPT — user spoke during TTS');
          tts.stop();
          claude.cancel();
          accumulatedTranscriptRef.current = text.trim();
          setState('listening');
          return;
        }

        accumulatedTranscriptRef.current += (accumulatedTranscriptRef.current ? ' ' : '') + text.trim();

        // Auto-send: after 3s of silence following speech, send to Claude
        if (stateRef.current === 'listening') {
          if (autoSendTimerRef.current) clearTimeout(autoSendTimerRef.current);
          autoSendTimerRef.current = setTimeout(() => {
            const accumulated = accumulatedTranscriptRef.current.trim();
            if (accumulated && stateRef.current === 'listening') {
              console.log('[Conversation] Auto-sending after silence:', accumulated);
              accumulatedTranscriptRef.current = '';
              deepgram.mute();
              setState('processing');
              claude.send(accumulated);
            }
          }, 3000);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No per-token TTS — wait for full response
  useEffect(() => {
    claude.onDeltaRef.current = () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When Claude finishes → send full text to ElevenLabs TTS (voice mode) or just display (text mode)
  useEffect(() => {
    claude.onCompleteRef.current = (fullText) => {
      if (!fullText.trim()) {
        goListening();
        return;
      }

      // In text mode, don't play TTS — just go back to ready/listening
      if (modeRef.current === 'text') {
        setState('idle');
        return;
      }

      // Strip email-draft blocks so TTS reads only the spoken confirmation,
      // not the full email body. The block still shows in the transcript.
      const spokenText = stripDraftBlocks(fullText);
      if (!spokenText) {
        // Response was only a draft block — nothing to speak aloud.
        goListening();
        return;
      }

      setState('speaking');

      // Mute mic during TTS to prevent echo
      deepgram.mute();

      tts.onDoneRef.current = () => {
        // TTS finished naturally → resume listening
        if (stateRef.current === 'speaking') {
          goListening();
        }
      };

      tts.speak(spokenText);

      // Unmute mic after a short delay so we can detect interruptions
      // But only if user hasn't manually muted
      setTimeout(() => {
        if (stateRef.current === 'speaking' && !userMutedRef.current) {
          deepgram.unmute();
        }
      }, 500);

      // Safety timeout
      setTimeout(() => {
        if (stateRef.current === 'speaking') {
          tts.stop();
          goListening();
        }
      }, 60000);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update TTS voice when settings change
  useEffect(() => {
    if (settings.voiceId) tts.setVoiceId(settings.voiceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.voiceId]);

  // Idle timeout
  const readyTimeoutRef = useRef(null);
  const clearReadyTimeout = useCallback(() => {
    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }
  }, []);

  /** goListening() — reconnect mic, enter listening state */
  const goListening = useCallback(() => {
    // In text mode, go to idle instead of listening
    if (modeRef.current === 'text') {
      setState('idle');
      return;
    }
    clearReadyTimeout();
    deepgram.resume();
    // If user has muted, keep mic muted after resume
    if (userMutedRef.current) {
      deepgram.mute();
    }
    accumulatedTranscriptRef.current = '';
    readyTimeoutRef.current = setTimeout(() => {
      if (stateRef.current === 'listening') {
        stop();
      }
    }, 120000);
    setState('listening');
  }, [deepgram, clearReadyTimeout]);

  /** stop() — full teardown back to idle */
  const stop = useCallback(() => {
    clearReadyTimeout();
    deepgram.disconnect();
    claude.cancel();
    tts.stop();
    accumulatedTranscriptRef.current = '';
    setState('idle');
  }, [deepgram, claude, tts, clearReadyTimeout]);

  /** start() — connect everything, go to listening */
  const start = useCallback(async () => {
    clearReadyTimeout();
    // Fetch ElevenLabs API key (needed even in text mode for potential mode switch)
    await tts.connect(settings.workerUrl);
    // In text mode, don't start mic
    if (modeRef.current === 'text') {
      setState('idle');
      return;
    }
    // Connect Deepgram + mic
    deepgram.connect(settings.workerUrl);
    accumulatedTranscriptRef.current = '';
    setState('listening');
  }, [deepgram, tts, settings.workerUrl, clearReadyTimeout]);

  /** startListening() — resume listening from ready state */
  const startListening = useCallback(() => {
    if (stateRef.current === 'ready') {
      clearReadyTimeout();
      accumulatedTranscriptRef.current = '';
      deepgram.connect(settings.workerUrl);
      setState('listening');
    }
  }, [deepgram, settings.workerUrl, clearReadyTimeout]);

  /** stopListening() — send accumulated transcript to Claude */
  const stopListening = useCallback(() => {
    if (stateRef.current !== 'listening') return;
    deepgram.mute();

    const text = accumulatedTranscriptRef.current.trim() || deepgram.partialTranscript.trim();
    accumulatedTranscriptRef.current = '';

    if (text) {
      setState('processing');
      claude.send(text, pendingImage || undefined);
      if (pendingImage && clearImage) clearImage();
    } else {
      deepgram.resume();
    }
  }, [deepgram, claude, pendingImage, clearImage]);

  /** sendText() — send typed text directly */
  const sendText = useCallback(async (text) => {
    if (!text.trim()) return;
    // In text mode, don't need to connect TTS/mic
    if (modeRef.current === 'voice' && stateRef.current === 'idle') {
      await tts.connect(settings.workerUrl);
    }
    clearReadyTimeout();
    setState('processing');
    claude.send(text.trim(), pendingImage || undefined);
    if (pendingImage && clearImage) clearImage();
  }, [claude, tts, settings.workerUrl, pendingImage, clearImage, clearReadyTimeout]);

  /** toggleUserMute() — user-controlled mic mute toggle */
  const toggleUserMute = useCallback(() => {
    const newMuted = !userMutedRef.current;
    userMutedRef.current = newMuted;
    setUserMuted(newMuted);
    if (newMuted) {
      deepgram.mute();
    } else {
      // Only unmute if we're in a state where mic should be active
      if (stateRef.current === 'listening') {
        deepgram.unmute();
      }
    }
  }, [deepgram]);

  /** switchMode(newMode) — switch between 'voice' and 'text' */
  const switchMode = useCallback((newMode) => {
    if (newMode === modeRef.current) return;
    modeRef.current = newMode;
    setMode(newMode);

    if (newMode === 'text') {
      // Switching to text mode — disconnect mic/STT, stop TTS
      if (stateRef.current !== 'idle') {
        stop();
      }
    }
    // Switching to voice mode — user will tap orb to start
  }, [stop]);

  const clearHistory = useCallback(() => {
    claude.clearHistory();
  }, [claude]);

  return {
    state,
    mode,
    userMuted,
    wordPulse: tts.wordPulse,
    transcript: deepgram.partialTranscript,
    streamedResponse: claude.streamedText,
    history: claude.history,
    micLevel: deepgram.micLevel,
    noiseGateOpen: deepgram.noiseGateOpen,
    start,
    stop,
    startListening,
    stopListening,
    sendText,
    clearHistory,
    toggleUserMute,
    switchMode,
    setNoiseGateThreshold: deepgram.setNoiseGateThreshold,
  };
}
